import React, { useState, useCallback, useRef } from 'react';
import { ArrowLeft, Video, Play, Pause, Square, Send, X, Globe, Users, Lock, Upload, Camera, Sparkles, Zap, Heart } from 'lucide-react';
import { useUI } from '../../contexts/UIContext';
import { useAuth } from '../../hooks/useAuth';
import withTranslation from '../../hoc/withTranslation';
import { SCREENS } from '../../utils/constants';

/**
 * Video Creation Screen - Futuristic Dopamine-Optimized Design
 * Next-gen interface for recording and sharing videos with iOS 26 Liquid Glass UI
 */
const VideoCreationScreen = ({ t, route }) => {
  const { navigateToScreen, isDarkMode, addFeedItem } = useUI();
  const { user } = useAuth();
  
  // Get privacy setting from navigation params
  const initialPrivacy = route?.params?.privacy || 'connections';
  
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [caption, setCaption] = useState('');
  const [privacy, setPrivacy] = useState(initialPrivacy);
  const [isUploading, setIsUploading] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const recordingInterval = useRef(null);

  const privacyOptions = [
    { id: 'public', label: t('privacy.public', 'Public'), icon: Globe, color: 'from-blue-400 to-cyan-400' },
    { id: 'connections', label: t('privacy.connections', 'Connections'), icon: Users, color: 'from-green-400 to-emerald-400' },
    { id: 'private', label: t('privacy.private', 'Private'), icon: Lock, color: 'from-purple-400 to-pink-400' }
  ];

  const handleFileSelect = useCallback((event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('video/')) {
      setSelectedVideo(file);
      const url = URL.createObjectURL(file);
      setVideoPreview(url);
    }
  }, []);

  const startVideoRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user', width: 1080, height: 1920 }, 
        audio: true 
      });
      
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      
      const chunks = [];
      mediaRecorder.ondataavailable = (event) => {
        chunks.push(event.data);
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'video/webm' });
        const url = URL.createObjectURL(blob);
        setVideoPreview(url);
        setSelectedVideo(blob);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start recording timer
      recordingInterval.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Error starting video recording:', error);
      alert('Unable to access camera. Please check permissions.');
    }
  }, []);

  const stopVideoRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (recordingInterval.current) {
        clearInterval(recordingInterval.current);
        recordingInterval.current = null;
      }
    }
  }, [isRecording]);

  const togglePlayback = useCallback(() => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  }, [isPlaying]);
  const handleShare = useCallback(async () => {
    if (!selectedVideo) return;
    
    setIsUploading(true);
    
    try {
      // Simulate upload process
      await new Promise(resolve => setTimeout(resolve, 2000));
        // Create new feed item
      const newPost = {
        type: 'media',
        user: {
          name: user?.name || 'You',
          profilePhoto: user?.profilePhoto || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150&h=150&fit=crop&crop=face&auto=format&q=80',
          avatar: user?.name?.charAt(0) || 'Y',
          verified: user?.verified || false
        },
        content: caption.trim() || 'Shared a video',
        videoUrl: videoPreview,
        duration: recordingTime,
        timestamp: new Date(),
        privacy: privacy,
        likes: 0,
        comments: [],
        shares: 0,
        tags: ['video']
      };
      
      // Add to feed
      addFeedItem(newPost);
      
      // Navigate back to feed
      navigateToScreen(SCREENS.IDENTITY_CIRCLE);
    } catch (error) {
      console.error('Error sharing video:', error);
    } finally {
      setIsUploading(false);
    }
  }, [selectedVideo, navigateToScreen, addFeedItem, user, caption, videoPreview, recordingTime, privacy]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const selectedPrivacyOption = privacyOptions.find(option => option.id === privacy);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 left-0 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
        <div className="absolute top-0 right-0 w-72 h-72 bg-yellow-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-pink-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
      </div>

      {/* Header */}
      <div className="relative z-10 flex items-center justify-between p-6">
        <button
          onClick={() => navigateToScreen(SCREENS.IDENTITY_CIRCLE)}
          className="p-3 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 
                   hover:bg-white/20 transition-all duration-300 group"
        >
          <ArrowLeft className="w-6 h-6 text-white group-hover:scale-110 transition-transform" />
        </button>
        
        <div className="flex items-center space-x-3">
          <Video className="w-8 h-8 text-purple-400" />
          <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
            Create Video
          </h1>
          <Sparkles className="w-6 h-6 text-yellow-400 animate-pulse" />
        </div>
        
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-purple-500 to-pink-500 
                      flex items-center justify-center">
          <Zap className="w-6 h-6 text-white animate-pulse" />
        </div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 px-6 py-4 space-y-6">
        {/* Video Preview/Recording Area */}
        <div className="aspect-[9/16] max-w-sm mx-auto rounded-3xl overflow-hidden 
                      bg-gradient-to-br from-gray-800 to-gray-900 border border-white/20 
                      backdrop-blur-xl shadow-2xl relative">
          {isRecording && (
            <div className="absolute top-4 left-4 z-20 flex items-center space-x-2 
                          bg-red-500/20 backdrop-blur-md border border-red-400/30 
                          rounded-full px-3 py-1">
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
              <span className="text-red-300 text-sm font-medium">{formatTime(recordingTime)}</span>
            </div>
          )}
          
          {videoPreview ? (
            <video
              ref={videoRef}
              src={videoPreview}
              className="w-full h-full object-cover"
              controls={false}
              autoPlay={isRecording}
              muted={isRecording}
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center space-y-4">
              <div className="w-24 h-24 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 
                            flex items-center justify-center animate-pulse">
                <Video className="w-12 h-12 text-white" />
              </div>
              <p className="text-white/60 text-center px-4">
                Ready to create an amazing video
              </p>
            </div>
          )}
          
          {/* Video Controls */}
          {videoPreview && !isRecording && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
              <button
                onClick={togglePlayback}
                className="p-4 rounded-full bg-white/10 backdrop-blur-md border border-white/20 
                         hover:bg-white/20 transition-all duration-300"
              >
                {isPlaying ? (
                  <Pause className="w-6 h-6 text-white" />
                ) : (
                  <Play className="w-6 h-6 text-white ml-1" />
                )}
              </button>
            </div>
          )}
        </div>

        {/* Recording Controls */}
        <div className="flex justify-center space-x-4">
          {!isRecording ? (
            <>
              <button
                onClick={() => fileInputRef.current?.click()}
                className="p-4 rounded-2xl bg-gradient-to-r from-blue-500 to-cyan-500 
                         hover:from-blue-600 hover:to-cyan-600 transition-all duration-300 
                         transform hover:scale-105 shadow-lg"
              >
                <Upload className="w-6 h-6 text-white" />
              </button>
              
              <button
                onClick={startVideoRecording}
                className="p-6 rounded-full bg-gradient-to-r from-red-500 to-pink-500 
                         hover:from-red-600 hover:to-pink-600 transition-all duration-300 
                         transform hover:scale-105 shadow-xl relative"
              >
                <Camera className="w-8 h-8 text-white" />
                <div className="absolute inset-0 rounded-full bg-red-400/20 animate-ping"></div>
              </button>
            </>
          ) : (
            <button
              onClick={stopVideoRecording}
              className="p-6 rounded-2xl bg-gradient-to-r from-red-500 to-pink-500 
                       hover:from-red-600 hover:to-pink-600 transition-all duration-300 
                       transform hover:scale-105 shadow-xl"
            >
              <Square className="w-8 h-8 text-white" />
            </button>
          )}
        </div>

        {/* Caption Input */}
        <div className="space-y-3">
          <label className="block text-white/80 font-medium">Add a caption</label>
          <div className="relative">
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="What's happening in your video?"
              className="w-full p-4 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 
                       text-white placeholder-white/50 resize-none focus:outline-none 
                       focus:ring-2 focus:ring-purple-400 focus:border-transparent 
                       transition-all duration-300"
              rows={3}
              maxLength={500}
            />
            <div className="absolute bottom-3 right-3 text-white/40 text-sm">
              {caption.length}/500
            </div>
          </div>
        </div>

        {/* Privacy Settings */}
        <div className="space-y-3">
          <label className="block text-white/80 font-medium">Who can see this?</label>
          <div className="grid grid-cols-3 gap-3">
            {privacyOptions.map((option) => {
              const Icon = option.icon;
              const isSelected = privacy === option.id;
              
              return (
                <button
                  key={option.id}
                  onClick={() => setPrivacy(option.id)}
                  className={`p-4 rounded-2xl border transition-all duration-300 transform hover:scale-105 ${
                    isSelected
                      ? `bg-gradient-to-r ${option.color} border-white/30 shadow-lg`
                      : 'bg-white/10 backdrop-blur-md border-white/20 hover:bg-white/20'
                  }`}
                >
                  <Icon className={`w-6 h-6 mx-auto mb-2 ${isSelected ? 'text-white' : 'text-white/60'}`} />
                  <p className={`text-sm font-medium ${isSelected ? 'text-white' : 'text-white/60'}`}>
                    {option.label}
                  </p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Share Button */}
        <button
          onClick={handleShare}
          disabled={!selectedVideo || isUploading}
          className={`w-full p-4 rounded-2xl font-bold text-lg transition-all duration-300 
                     transform hover:scale-105 shadow-xl flex items-center justify-center space-x-3 ${
            selectedVideo && !isUploading
              ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white'
              : 'bg-gray-600/50 text-gray-400 cursor-not-allowed'
          }`}
        >
          {isUploading ? (
            <>
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              <span>Sharing...</span>
            </>
          ) : (
            <>
              <Send className="w-6 h-6" />
              <span>Share Video</span>
              <Heart className="w-5 h-5 animate-pulse" />
            </>
          )}
        </button>
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="video/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      <style jsx>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default withTranslation(VideoCreationScreen);
